/*
 * The language dropdown, in the header of both shells (S48, 2026-10-29).
 *
 * The owner placed it "in the header bar, to the left of light/dark mode toggle",
 * with two options: the US flag and EN for "en-us", the Danish flag and DA for
 * "da-dk". Like the light/dark switch it sits outside the admin sign-in gate,
 * because the sign-in cards are translated too and whoever is reading them may
 * want the other language. The choice and its memory are language.ts's.
 *
 * WHY NOT A <select>. A native option holds text only, and the owner asked for
 * flags. Nor emoji flags: Windows draws the regional-indicator pair as the two
 * letters "US" rather than a flag, so the flags are drawn here, as small SVGs.
 *
 * A MENU BUTTON. The button names the language the page is in - "Language:
 * English (EN)" - and opens a menu of menuitemradio items, the current one
 * checked. Opening moves focus to the checked item; the arrow keys, Home and End
 * move between items; Enter, Space or a click chooses and closes; Escape closes
 * and gives focus back to the button; and focus leaving the switch - Tab, or a
 * click anywhere else - closes it. Each item is named in its own language with
 * its code, "Dansk (DA)", and carries that language's `lang`, so a screen reader
 * pronounces it as written.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { LANGUAGES, useLanguage, type Language, type LanguageCode } from '../i18n/language.js';

function optionName(language: Language): string {
  return `${language.name} (${language.short})`;
}

export function LanguageSwitch(): JSX.Element {
  const { language, choose } = useLanguage();
  const t = language.messages;
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);

  // On opening, focus goes to the language the page is in.
  useEffect(() => {
    if (!open) return;
    const current = LANGUAGES.findIndex((option) => option.code === language.code);
    items.current[current]?.focus();
    // Only on opening: choosing closes the menu anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (focusButton: boolean) => {
    setOpen(false);
    if (focusButton) button.current?.focus();
  };

  const pick = (code: LanguageCode) => {
    choose(code);
    close(true);
  };

  const focusItem = (index: number) => {
    const count = LANGUAGES.length;
    items.current[(index + count) % count]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const focused = items.current.findIndex((item) => item === document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(focused + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(focused - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(LANGUAGES.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className="language-switch"
      ref={root}
      onBlur={(event) => {
        // Focus moving anywhere outside the switch closes the menu - Tab out of
        // it, or a click elsewhere on the page.
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !root.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        className="language-switch-button"
        aria-haspopup="menu"
        aria-expanded={open}
        {...(open ? { 'aria-controls': menuId } : {})}
        aria-label={t.language.current(optionName(language))}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Flag code={language.code} />
        <span>{language.short}</span>
        <svg className="language-switch-caret" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul id={menuId} className="language-switch-menu" role="menu" aria-label={t.language.label} onKeyDown={onMenuKeyDown}>
          {LANGUAGES.map((option, index) => (
            <li key={option.code} role="none">
              <button
                ref={(element) => {
                  items.current[index] = element;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={option.code === language.code}
                aria-label={optionName(option)}
                lang={option.htmlLang}
                tabIndex={-1}
                onClick={() => pick(option.code)}
              >
                <Flag code={option.code} />
                <span>{option.short}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/*
 * The two flags, drawn in one 20 x 14 box so the codes beside them line up -
 * exported for the Settings page's language columns (S49). The
 * Dannebrog keeps its own proportions; the Stars and Stripes keeps its thirteen
 * stripes and its canton at 40% of the width, with the fifty stars as dots - at
 * this size a star's points are below a pixel either way.
 */
export function Flag({ code }: { code: LanguageCode }): JSX.Element {
  return (
    <svg className="flag" width="20" height="14" viewBox="0 0 20 14" aria-hidden="true" focusable="false">
      {code === 'da-dk' ? <DanishFlag /> : <UsFlag />}
    </svg>
  );
}

function DanishFlag(): JSX.Element {
  return (
    <>
      <rect width="20" height="14" fill="#c8102e" />
      <rect x="6.5" width="2" height="14" fill="#ffffff" />
      <rect y="6" width="20" height="2" fill="#ffffff" />
    </>
  );
}

const STRIPE = 14 / 13;
const CANTON = { width: 8, height: STRIPE * 7 };

/** Nine rows of stars, six and five in turn, on the canton's half-step grid. */
const STARS = Array.from({ length: 9 }, (_, row) =>
  Array.from({ length: row % 2 === 0 ? 6 : 5 }, (_, column) => ({
    x: ((row % 2 === 0 ? 2 * column + 1 : 2 * column + 2) * CANTON.width) / 12,
    y: ((row + 1) * CANTON.height) / 10,
  })),
).flat();

function UsFlag(): JSX.Element {
  return (
    <>
      <rect width="20" height="14" fill="#b22234" />
      {Array.from({ length: 6 }, (_, index) => (
        <rect key={index} y={STRIPE * (2 * index + 1)} width="20" height={STRIPE} fill="#ffffff" />
      ))}
      <rect width={CANTON.width} height={CANTON.height} fill="#3c3b6e" />
      {STARS.map((star) => (
        <circle key={`${star.x}-${star.y}`} cx={star.x} cy={star.y} r="0.22" fill="#ffffff" />
      ))}
    </>
  );
}
